import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { spaces } from '../../../../assets/styles/themes/tokens';
import { currencyFormat } from '../../../../utils/format';
import { TERM_MONTHS } from '../checkout.constants';
import { deriveBilling, toPerMonth } from '../checkout.helpers';
import type { StripeCheckout } from '../checkout.types';

/**
 * Post-trial billing notice shown under the submit button in trial mode:
 * how much / when the user is charged once the trial ends.
 */
const TrialCostNotice: React.FC<{ checkout: StripeCheckout | null }> = ({ checkout }) => {
	const { t, i18n } = useTranslation();

	if (!checkout) return null;
	const { trial, isYearly, billing } = deriveBilling(checkout);
	if (!trial) return null;

	const price = currencyFormat(toPerMonth(billing.total.minorUnitsAmount, isYearly) / 100);
	const endDate = new Date(trial.trialEnd * 1000).toLocaleDateString(i18n.language);

	return (
		<Box
			data-testid="stripe-checkout-trial-cost"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: spaces.xs,
				textAlign: 'center',
				color: '#493666',
			}}
		>
			<Typography variant="caption">{t('Chargeable after the trial period')}</Typography>
			<Typography variant="caption">
				{t(
					'After the {{days}} day test phase, you will be charged €{{price}} a month if you do not cancel' +
						' your trial subscription. This is valid from {{endDate}}, for a duration of {{months}} months',
					{ days: trial.trialPeriodDays, price, endDate, months: TERM_MONTHS }
				)}
			</Typography>
		</Box>
	);
};

export default TrialCostNotice;
